import React, { useState } from 'react';
import {
Badge,
Box,
Button,
Card,
Checkbox,
Divider,
Flex,
Group,
Input,
Loader,
Modal,
Select,
Table,
Text,
Textarea,
TextInput,
Title,
} from '@mantine/core';
import { IconCheck, IconEdit, IconTrash } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react';
import { formatDate, formatDateTime } from '@medplum/core';
import { DateInput } from '@mantine/dates';
import { size } from 'lodash';

interface Props {
resources: any[];
createCarePlan: any;
loading:boolean
}

const CarePlanComponent: React.FC<Props> = ({ resources, createCarePlan,loading }) => {
const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
const [isModalOpen, setIsModalOpen] = useState(false);
const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
const [resourceToDelete, setResourceToDelete] = useState<any | null>(null);
const [newResourceType, setNewResourceType] = useState<'Goal' | 'Task' | ''>('');
const [newDescription, setNewDescription] = useState<string>('');
const [allResources, setAllResources] = useState<any[]>(resources);
const [newStartDate, setNewStartDate] = useState<Date | null>(null);
const [newEndDate, setNewEndDate] = useState<Date | null>(null);
const [editGoalIndex, setEditGoalIndex] = useState<number | null>(null);
const [editTaskIndex, setEditTaskIndex] = useState<number | null>(null);
const [tempDescription, setTempDescription] = useState<string>('');
const [tempStartDate, setTempStartDate] = useState<string>('');
const [tempEndDate, setTempEndDate] = useState<string>('');
const [goalStartDate, setGoalStartDate] = useState('');

const medplum = useMedplum();
const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
if (type === 'start') {
setTempStartDate(event.target.value);
} else {
setTempEndDate(event.target.value);
}
};

const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
setTempDescription(event.target.value);
};

const handleSaveClick = (resource: any, type: 'Goal' | 'Task') => {
if (type === 'Goal') {
resource.description.coding[0].code=tempDescription
resource.description.coding[0].display=tempDescription

resource.startDate = goalStartDate; 
} else if (type === 'Task') {
resource.description = tempDescription;
resource.executionPeriod.start=tempStartDate
resource.executionPeriod.end=tempEndDate

}
setEditGoalIndex(null);
setEditTaskIndex(null);

};
const handleCheckboxChange = (resource: any, type: 'Goal' | 'Task') => {
if (type === 'Goal') {
setSelectedGoals((prev) =>
prev.includes(resource) ? prev.filter((item) => item !== resource) : [...prev, resource]
);
} else {
setSelectedTasks((prev) =>
prev.includes(resource) ? prev.filter((item) => item !== resource) : [...prev, resource]
);
}
};
const handleAddResource = () => {
if (newResourceType && newDescription.trim()) {
let newResource: any;
if (newResourceType === 'Goal') {
newResource = {
resourceType: 'Goal',
description: {
coding: [
{
code: newDescription,
display: newDescription,
}
]
},
lifecycleStatus: 'active',
startDate: newStartDate ? newStartDate.toISOString().split('T')[0] : '',
subject: {
reference: 'Patient/073c61c9-9d12-4416-ab71-ae8c60cdf2ef'
}
};
} else if (newResourceType === 'Task') {
newResource = {
resourceType: 'Task',
status: 'pending',
intent: 'order',
description: newDescription,
for: {
reference: 'Patient/073c61c9-9d12-4416-ab71-ae8c60cdf2ef'
},
executionPeriod: {
start: newStartDate ? newStartDate : '',
end: newEndDate ? newEndDate: ''
}
};
}
// Add the new resource to the array
setAllResources((prev) => (prev ? [...prev, newResource] : [newResource]));
// Reset the input fields and close the modal
setNewResourceType('');
setNewDescription('');
setNewStartDate(null);
setNewEndDate(null);
setIsModalOpen(false);
}
};
const renderDescription = (description: any) => {
if (typeof description === 'string') {
return description;
} else if (description && Array.isArray(description.coding)) {
return description.coding.map((coding:any) => coding.display).join(', ');
} else {
return 'Unknown description';
}
};

const handleEditClick = (index: number, resource: any, type: 'Goal' | 'Task') => {
const descriptionText = typeof resource.description === 'string' ? resource.description : renderDescription(resource.description);
if (type === 'Goal') {
setGoalStartDate(resource.startDate); 
setEditGoalIndex(index);
setEditTaskIndex(null);
} else {
setEditTaskIndex(index);
setEditGoalIndex(null);
}
setTempDescription(descriptionText);
setTempStartDate(resource.executionPeriod?.start || '');
setTempEndDate(resource.executionPeriod?.end || '');
};
const handleDeleteClick = (resource: any) => {
setResourceToDelete(resource);
setIsDeleteModalOpen(true);
};

const handleConfirmDelete = () => {
if (resourceToDelete) {
setAllResources((prev) => prev.filter((resource) => resource !== resourceToDelete));
setSelectedGoals((prev) => prev.filter((resource) => resource !== resourceToDelete))
setSelectedTasks((prev) => prev.filter((resource) => resource !== resourceToDelete))

setResourceToDelete(null);
setIsDeleteModalOpen(false);
}
};
const hasGoals = Array.isArray(allResources) && allResources.some((resource) => resource?.resourceType === 'Goal');
const hasTasks = Array.isArray(allResources) && allResources.some((resource) => resource?.resourceType === 'Task');

const capitalizeFirstLetter = (text: string) => {
if (!text) return '';
return text.charAt(0).toUpperCase() + text.slice(1);
};

return (
<Box p="md">
<Flex justify="space-between" align="center" mb="lg">
<Title order={2}>Care Plan</Title>
</Flex>

<Box>
{/* Goals Section */}
<Box mb="md">
<Title order={4} mb="sm">Goals</Title>
{!hasGoals ? (
<Text>No goals available. Please add a goal.</Text>
) : (
<Table striped highlightOnHover withBorder>
<thead>
<tr>
<th style={{ width: '10%', minWidth: '50px', maxWidth: '80px' }}>Select</th>
<th style={{ width: '35%', minWidth: '150px', maxWidth: '300px' }}>Description</th>
<th style={{ width: '15%', minWidth: '80px', maxWidth: '120px' }}>Status</th>
<th style={{ width: '20%', minWidth: '100px', maxWidth: '150px' }}>Start Date</th>
<th style={{ width: '20%', minWidth: '120px', maxWidth: '180px' }}>Actions</th>
</tr>
</thead>
<tbody>
{allResources?.filter((resource) => resource.resourceType === 'Goal').map((goal, index) => (
<tr key={goal.id || index}>
<td>
<Checkbox
checked={selectedGoals.includes(goal)}
onChange={() => handleCheckboxChange(goal, 'Goal')}
/>
</td>
<td>
{editGoalIndex === index ? (
<Input
value={tempDescription}
onChange={handleInputChange}
size="sm"
sx={{ flex: 1 }}
/>
) : (
<Text>{capitalizeFirstLetter(renderDescription(goal.description))}</Text>
)}
</td>
<td style={{ maxWidth: '30px' }}>
<Badge size="sm" color="green" variant="light">
{goal.lifecycleStatus}
</Badge>
</td>
<td>
{editGoalIndex === index ? (
<Input
type="date"
value={goalStartDate}
onChange={(e) => setGoalStartDate(e.target.value)}
size="sm"
/>
) : (
formatDate(goal.startDate)
)}
</td>
<td>
{editGoalIndex === index ? (
<Button size="xs" color="blue" onClick={() => handleSaveClick(goal, 'Goal')}>
<IconCheck size={16} />
</Button>
) : (
<>
<IconEdit
color="#228be6"
size={16}
onClick={() => handleEditClick(index, goal, 'Goal')}
style={{ cursor: 'pointer' }}
/>
<Button size="xs" variant="unstyled" onClick={() => handleDeleteClick(goal)}>
<IconTrash color="red" size={16} />
</Button>
</>
)}
</td>
</tr>
))}
</tbody>
</Table>
)}
</Box>

{/* Tasks Section */}
<Box>
<Title order={4} mb="sm">Tasks</Title>
{!hasTasks ? (
<Text>No tasks available. Please add a task.</Text>
) : (
<Table striped highlightOnHover withBorder>
<thead>
<tr>
<th style={{ width: '10%', minWidth: '50px', maxWidth: '80px' }}>Select</th>
<th style={{ width: '35%', minWidth: '150px', maxWidth: '300px' }}>Description</th>
<th style={{ width: '15%', minWidth: '80px', maxWidth: '120px' }}>Status</th>
<th style={{ width: '20%', minWidth: '100px', maxWidth: '150px' }}>Start Date</th>
{/* <th style={{ width: '20%', minWidth: '100px', maxWidth: '150px' }}>End Date</th> */}
<th style={{ width: '20%', minWidth: '120px', maxWidth: '180px' }}>Actions</th>
</tr>
</thead>
<tbody>
{allResources?.filter((resource) => resource.resourceType === 'Task').map((task, index) => (
<tr key={index}>
<td>
<Checkbox
checked={selectedTasks.includes(task)}
onChange={() => handleCheckboxChange(task, 'Task')}
/>
</td>
<td>
{editTaskIndex === index ? (
<Input
value={tempDescription}
onChange={handleInputChange}
size="sm"
sx={{ flex: 1 }}
/>
) : (
<Text>{capitalizeFirstLetter(renderDescription(task.description))}</Text>
)}
</td>
<td style={{ minWidth: '80px', maxWidth: '120px',}}>
<Badge size="sm" color="green" variant="light">
{task.status}
</Badge>
</td>
<td>
{editTaskIndex === index ? (
<Input
value={tempStartDate}
onChange={(e) => handleDateChange(e, 'start')}
type="date"
size="sm"
/>
) : (
<Text size="sm">{formatDate(task.executionPeriod?.start)}</Text>
)}
</td>
{/* <td>
{editTaskIndex === index ? (
<Input
value={tempEndDate}
onChange={(e) => handleDateChange(e, 'end')}
type="date"
size="sm"
/>
) : (
<Text size="sm">{formatDate(task.executionPeriod?.end)}</Text>
)}
</td> */}
<td>
{editTaskIndex === index ? (
<Button size="xs" color="blue" onClick={() => handleSaveClick(task, 'Task')}>
<IconCheck size={16} />
</Button>
) : (
<>
<IconEdit
color="#228be6"
size={16}
onClick={() => handleEditClick(index, task, 'Task')}
style={{ cursor: 'pointer' }}
/>
<Button size="xs" variant="unstyled" onClick={() => handleDeleteClick(task)}>
<IconTrash color='red' size={14} />
</Button>
</>
)}
</td>
</tr>
))}
</tbody>
</Table>
)}
</Box>
</Box>

<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
<Button variant='outline' onClick={() => setIsModalOpen(true)}>Add New</Button>
<Button 
ml={'10px'} 
disabled={[...selectedGoals,...selectedTasks].length===0 || loading} 
onClick={() => createCarePlan([...selectedGoals, ...selectedTasks],)}
>
{loading?<Loader size={'sm'} color='blue'/>:'Submit Care Plan'}
</Button>
</Box>
{/* Modal for Creating New Resource */}
<Modal
size={'lg'}
centered
opened={isModalOpen}
onClose={() => setIsModalOpen(false)}
title={`Create a new ${newResourceType}`}
>
<Box mt={'10px'} sx={{display:"flex",flexDirection:"column",gap:"8px"}}>

<Select
withAsterisk
label="Select Type"
data={['Goal', 'Task']}
value={newResourceType}
onChange={(value: 'Goal' | 'Task' | '') => setNewResourceType(value)}

/>
<DateInput
clearable
label="Start Date"
value={newStartDate}
onChange={setNewStartDate}

/>
{newResourceType === 'Task' && (
<DateInput
clearable
label="End Date"
value={newEndDate}
onChange={setNewEndDate}
/>
)}
<Textarea
label="Description"
value={newDescription}
onChange={(e) => setNewDescription(e.target.value)}
size="md"
minRows={5}
maxRows={5}
withAsterisk
// mt="md"
/>

<Box sx={{display:"flex",justifyContent:"flex-end"}}>
<Button mt="sm" onClick={handleAddResource}>
Add {newResourceType}
</Button>

</Box>
</Box>
</Modal>
<Modal
opened={isDeleteModalOpen}
onClose={() => setIsDeleteModalOpen(false)}
title="Confirm Deletion"
>
<Text>Are you sure you want to delete this item?</Text>
<Group position="right" mt="md">
<Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
Cancel
</Button>
<Button color="red" onClick={handleConfirmDelete}>
Confirm
</Button>
</Group>
</Modal>
</Box>
);
};

export default CarePlanComponent;
